import { useEffect, useState } from "react";
import { ColumnItem } from "../../../common/models/table";
import { ActionsKey } from "../../../common/models/Actions";
import { HttpStatusCode } from "axios";
import dayjs from "dayjs";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { FaFileAlt } from "react-icons/fa";
import { Loader } from "../../../common/components/Loader/Loader";
import { TitleHeader } from "../../../common/components/TitleHeader/TitleHeader";
import { LFTableV3 } from "../../../common/components/LFTable/LFTableV3";
import { CustomDrawer } from "../../../common/components/Drawer/CustomDrawer";
import { StatusTag } from "../../../common/components/Status/StatusTag";
import { downloadPDF } from "../../../utilities/downloadPdf";
import { API_URLs } from "../../../utilities/API_URLs";
import { getRowId } from "../../../common/utils/iri";
import {
  useReleaseOrdersListState,
  useReleaseOrdersStore,
} from "../store/_releaseOrdersStore";
import { ReleaseOrderService } from "../store/_requests";
import {
  ExitNoteRemovalTypeConstant,
  ExitNoteStateConstant,
  RELEASE_ORDER_CONFIG,
} from "../core/_constant.releaseOrder";
import { RemovalType } from "../core/_model.releaseOrder";
import { ExitNotePdfViewer } from "./ExitNotePdfViewer";
import { CancelExitNoteAction } from "./CancelExitNoteAction";
import { CreateReleaseOrderForm } from "./CreateReleaseOrderForm";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

type ReleaseOrdersListProps = {
  /** CONTAINER in the container park, GOODS in the warehouse. */
  removalType: RemovalType;
};

export const ReleaseOrdersList = ({ removalType }: ReleaseOrdersListProps) => {
  const {
    setReleaseOrdersList,
    releaseOrdersList,
    actioned,
    totalItems,
    setActioned,
  } = useReleaseOrdersStore();
  const { pageState, setPageState, resetPageState } =
    useReleaseOrdersListState();

  const config = RELEASE_ORDER_CONFIG[removalType];

  const [isShowDetails, setIsShowDetails] = useState<boolean>(false);
  const [isShowCreateForm, setIsShowCreateForm] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const fetchAllReleaseOrders = async (params?: any) => {
    try {
      setIsLoading(true);
      const res = await ReleaseOrderService.getReleaseOrderList(
        params,
        removalType
      );
      if (res?.status === HttpStatusCode.Ok) {
        setReleaseOrdersList(res?.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllReleaseOrders(pageState);
  }, [pageState, removalType]);

  /**
   * Le détail et le fichier s'appuient sur le même document : l'œil l'affiche
   * dans un drawer, l'icône fichier le télécharge.
   */
  const onActionClick = (item: any, action: ActionsKey) => {
    setActioned({ data: { ...item }, action });
    setIsShowDetails(true);
  };

  const onClickDownload = async (row: any) => {
    const rowId = getRowId(row);

    if (!rowId) return;

    try {
      setDownloadingId(rowId);
      await downloadPDF(
        API_URLs.exitNote.exitNotePdfUrl.replace("{id}", String(row.id))
      );
    } catch (error) {
      console.log(error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSetParams = (params: any) => {
    setPageState({
      ...params,
    });
  };

  const handleReleaseOrderCreated = () => {
    setIsShowCreateForm(false);
    fetchAllReleaseOrders(pageState);
  };

  const renderRemovalCount = (record: any) => {
    const details: string[] = [];

    if (record?.containerCount) {
      details.push(`${record.containerCount} TC`);
    }
    if (record?.goodsRemovalCount) {
      details.push(`${record.goodsRemovalCount} march.`);
    }

    return (
      <div className="d-flex flex-column align-items-center">
        <span>{record?.removalCount ?? 0}</span>
        {details.length > 1 && (
          <small className="text-muted">{details.join(" · ")}</small>
        )}
      </div>
    );
  };

  const cols: ColumnItem[] = [
    {
      key: "reference",
      title: "Référence",
      dataIndex: "reference",
      show: true,
    },
    ...( removalType === "CONTAINER" ? [
      {
        key: "customer",
        title: "Transitaire",
        dataIndex: "customer",
        nestedKey: "customer.name",
        show: true,
        render: (customer: any) => {
          return <span>{customer?.name ?? "-"}</span>;
        },
      } as ColumnItem,
      {
        key: "finalCustomer",
        title: "Client",
        dataIndex: "finalCustomer",
        show: true,
      } as ColumnItem,
    ] : [
      {
        key: "customer",
        title: "Transitaire / Client",
        dataIndex: "customer",
        nestedKey: "customer.name",
        show: true,
        render: (customer: any) => {
          return <span>{customer?.name ?? "-"}</span>;
        },
      } as ColumnItem,
    ]),
    {
      key: "exitDate",
      title: "Date de sortie",
      dataIndex: "exitDate",
      show: true,
      render: (exitDate: any) => {
        const date = exitDate ? dayjs(exitDate, "DD/MM/YYYY HH:mm:ss").format("DD/MM/YYYY") : "-";
        return (
          <span>{date}</span>
        );
      },
    },
    {
      key: "removalCount",
      title: "Nombre de sorties",
      dataIndex: "removalCount",
      show: true,
      render: (_: any, record: any) => renderRemovalCount(record),
    },
    {
      key: "state",
      title: "Statut",
      dataIndex: "state",
      nestedKey: "state.name",
      show: true,
      render: (state: any) => {
        return (
          <StatusTag
            value={state?.id}
            options={ExitNoteStateConstant.OPTIONS}
          />
        );
      },
    },
    {
      key: "generatedBy",
      title: "Généré par",
      dataIndex: "generatedBy",
      nestedKey: "generatedBy.fullname",
      show: true,
      render: (generatedBy: any) => {
        return <span>{generatedBy?.fullname ?? "-"}</span>;
      },
    },
    {
      key: "cancelReason",
      title: "Motif d'annulation",
      dataIndex: "cancelReason",
      show: true,
    },
  ];

  return (
    <div className="">
      <Loader isLoading={isLoading} />
      <TitleHeader
        title={config.title}
        isEnableAdd
        onClickOnAdd={() => setIsShowCreateForm(true)}
      />

      <div className="mt-4">
        <div className="">
          <LFTableV3
            columns={cols}
            data={releaseOrdersList}
            params={pageState}
            onAction={onActionClick}
            isCanShow={() => true}
            onSearch={handleSetParams}
            onResetSearch={resetPageState}
            pagination={{
              defaultCurrentPage: pageState.page,
              itemsPerPage: pageState?.itemsPerPage,
              totalItems: totalItems,
            }}
            onChangePage={handleSetParams}
            otherActions={[
              (rowAction) => {
                const rowData = rowAction.row;
                const rowId = getRowId(rowData);

                return (
                  <div>
                    {downloadingId === rowId ? (
                      <Spin
                        indicator={<LoadingOutlined spin />}
                        size="small"
                      />
                    ) : (
                      <div
                        className=" cursor-pointer"
                        onClick={() => {
                          onClickDownload(rowData);
                        }}
                      >
                        <FaFileAlt title={"Télécharger le bon de sortie"} />
                      </div>
                    )}
                  </div>
                );
              },
              (rowAction) => {
                const rowData = rowAction.row;

                if (rowData?.state?.id !== ExitNoteStateConstant.GENERATED_ID) {
                  return null;
                }

                return (
                  <CancelExitNoteAction
                    row={rowData}
                    onCancelled={() => fetchAllReleaseOrders(pageState)}
                  />
                );
              },
            ]}
          />
        </div>
      </div>

      {/* Drawer des détails : aperçu du bon de sortie */}
      {isShowDetails ? (
        <CustomDrawer
          isOpen={isShowDetails}
          onClose={() => setIsShowDetails(false)}
          title={"Détails du bon de sortie"}
          width={"80%"}
        >
          <ExitNotePdfViewer
            exitNoteId={actioned?.data?.id ?? null}
            reference={actioned?.data?.reference}
            exitDate={actioned?.data?.exitDate}
            state={actioned?.data?.state}
          />
        </CustomDrawer>
      ) : (
        ""
      )}

      {/* Drawer de création */}
      {isShowCreateForm ? (
        <CustomDrawer
          isOpen={isShowCreateForm}
          onClose={() => setIsShowCreateForm(false)}
          title={"Nouveau bon de sortie"}
          width={"75%"}
        >
          <CreateReleaseOrderForm
            removalType={removalType}
            onCreated={handleReleaseOrderCreated}
            onClose={() => setIsShowCreateForm(false)}
          />
        </CustomDrawer>
      ) : (
        ""
      )}
    </div>
  );
};
